/*
 * Shared helpers for the instant task tier — the elapsed timer, its colour
 * thresholds, and the expiry countdown.
 *
 * One module because the same timer renders in three places (the list's due
 * cell, the task detail banner, and Wave 3's broadcast pop-up); three copies of
 * "amber at ten minutes" would drift the first time a threshold moved.
 *
 * Every instant is computed client-side from the RFC3339 UTC strings the server
 * already sends (created_at_iso, instant_expires_at_iso), the same way
 * overdue.js derives the overdue highlight: no extra payload, and the value
 * stays correct as the clock passes a threshold between renders.
 *
 * `now` is a parameter rather than read from the clock inside these functions so
 * the callers' existing per-second tick is the single source of time and the
 * tests do not have to freeze the clock.
 */

import moment from 'moment-timezone';
import { is_terminal as isTerminal } from './overdue';

// PRD thresholds: black under 10 minutes, amber 10-30, red past 30.
export const TIMER_AMBER_MS = 10 * 60 * 1000;
export const TIMER_RED_MS = 30 * 60 * 1000;

// Token, not a colour: the caller maps it to the page's CSS variables, so the
// thresholds stay independent of the theme.
export type TimerColor = 'black' | 'amber' | 'red';

// The slice of a list row / relay item the instant helpers read.
export interface InstantItem {
  status?: string;
  is_instant?: boolean;
}

export const timerColor = (ms: unknown): TimerColor => {
  const elapsed = typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
  if (elapsed >= TIMER_RED_MS) return 'red';
  if (elapsed >= TIMER_AMBER_MS) return 'amber';
  return 'black';
};

const COLOR_VARS: Record<TimerColor, string> = {
  black: 'var(--text-primary)',
  amber: 'var(--state-warning)',
  red: 'var(--state-error)'
};

export const timer_color_css = (ms: unknown): string => COLOR_VARS[timerColor(ms)];

// Milliseconds since the task was created. null when the row carries no
// parseable creation instant — the caller then renders nothing rather than a
// timer counting from the epoch.
export const elapsed_ms = (
  createdAtIso: string | null | undefined,
  now: moment.MomentInput
): number | null => {
  if (!createdAtIso) return null;
  const created = moment(createdAtIso);
  if (!created.isValid()) return null;
  // Clock skew between the server and the viewer can make a just-created task
  // look like it is from the future; a timer must never run backwards.
  return Math.max(0, moment(now).diff(created));
};

const pad = (n: number): string => String(n).padStart(2, '0');

// HH:MM:SS. Hours are not capped at 24 — a task left alone overnight should read
// as absurd, not wrap around to something that looks fresh.
export const format_elapsed = (ms: unknown): string => {
  const total = Math.floor(
    Math.max(0, typeof ms === 'number' && Number.isFinite(ms) ? ms : 0) / 1000
  );
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${pad(hours)}:${pad(minutes)}:${pad(total % 60)}`;
};

// "3h 46m" / "46m" until auto-completion. Empty string when there is no expiry,
// it is unparseable, or it has already passed — the caller words those cases
// itself rather than rendering "expires in 0m".
export const format_remaining = (
  expiresAtIso: string | null | undefined,
  now: moment.MomentInput
): string => {
  if (!expiresAtIso) return '';
  const expires = moment(expiresAtIso);
  if (!expires.isValid()) return '';
  const ms = expires.diff(moment(now));
  if (ms <= 0) return '';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

// A configured expiry, worded the way the wireframe words it: "4h" rather than
// "240m", so an operator reading "EXPIRY DURATION 4 HOURS" and the helper line
// below the box sees the same quantity. Minutes stay minutes under an hour,
// which is where the control's own unit lives.
export const format_duration_minutes = (minutes: unknown): string => {
  const total = Math.floor(Number(minutes));
  if (!Number.isFinite(total) || total <= 0) return '';
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
};

// An instant task still worth a timer: terminal work (submitted, auto-closed,
// auto-completed) is finished, so its urgency is history.
export const is_instant_live = (item: InstantItem | null | undefined): boolean =>
  !!item && !!item.is_instant && !isTerminal(item);

// Index of the first row outside the live-instant block, the list's leading
// sort key (goserver listLiveExpr); -1 when every row is live instant.
export const first_standard_index = (
  rows: Array<InstantItem | null | undefined> | null | undefined
): number => (rows || []).findIndex((item) => !is_instant_live(item));

// The wireframe's pop-up wording for the instant rules it names; every other
// type keeps the server's rule label.
const SHORT_LABELS: Record<string, string> = {
  FRESH_LEAD: 'New Sign-up',
  NO_BOOK: 'No Booking',
  LOGIN_ISSUE: 'Login Issue',
  CHURNED_MEMBER_LOGIN: 'Churned Member',
  LEAD_OTP_NOT_VERIFIED: 'Unverified Lead',
  BOOKING_ERROR: 'Booking Error'
};

export const short_label = (
  taskType: string | null | undefined,
  taskLabel?: string | null
): string => (taskType && SHORT_LABELS[taskType]) || taskLabel || taskType || '';
